// ============================================================================
// PRICING TRUST — which price-list rows may put money on a quote line
// ----------------------------------------------------------------------------
// One rule for every producer and for the Stage 5 certifier:
//
//   Visible  — the row is shown on the Pricing page (isVisiblePricingItem).
//   Trusted  — the row may price a quote line (isTrustedPricingItem).
//
// "AI Review" provenance and `estimate` rows are visible review inventory, not
// commercial authority. Stage 5 certifies every rate-book line against the
// trusted projection only (ratesheet-fingerprint.js canonicalRatesheetAuthority),
// so a producer that accepted a visible-but-untrusted row wrote a line Stage 5
// could never certify ("current_pricing_row_missing"). Every place that offers
// or accepts a row as pricing authority must use isTrustedPricingItem.
//
// Pure and import-free on purpose: Stage 3 (wms-generator.js) and the Francis
// coverage normaliser need this rule without loading the pricing data layer.
// pricing.js re-exports these on BusinessPricingService. The builder's
// approval of an estimate row lives below, beside the markers it clears.
// ============================================================================

export function isHiddenPricingItem(item) {
    const status = String(item?.review_status ?? item?.reviewStatus ?? '').trim().toLowerCase();
    const source = String(item?.source ?? item?.origin ?? '').trim().toLowerCase();
    return status === 'pending_review'
        || status === 'formula_component'
        || status === 'retired'
        || item?.selectable_for_scope === false
        || item?.selectableForScope === false
        || source === 'ai_discovered';
}

export function isPendingReviewPricingItem(item) {
    const source = String(item?.source ?? item?.origin ?? '').trim().toLowerCase();
    const sourceRating = String(item?.source_rating ?? item?.sourceRating ?? '').trim().toLowerCase();
    const sourceNote = String(item?.source_note ?? item?.sourceNote ?? '').trim().toLowerCase();
    return isHiddenPricingItem(item)
        || source === 'ai review'
        || sourceRating === 'estimate'
        || sourceNote === 'ai review';
}

export function isTrustedPricingItem(item) {
    return !!item && !isPendingReviewPricingItem(item);
}

export function isVisiblePricingItem(item) {
    return !!item && !isHiddenPricingItem(item);
}

export function trustedPricingItems(items) {
    return Array.isArray(items) ? items.filter(isTrustedPricingItem) : [];
}

// ============================================================================
// BUILDER APPROVAL OF AN ESTIMATE ROW (Peter, 2026-10-20)
// ----------------------------------------------------------------------------
// An estimate row is visible on the Pricing page but may not price a quote
// line: "AI Review" provenance or an `estimate` rating. The builder settles it
// in one of two ways, both of which make the rate the builder's own:
//
//   approve    — the Approve button (Review Queue or Price List).
//   rate_edit  — the builder types a new sell rate for the row in the Pricing
//                edit dialog (PricingPage saveModal). A name, unit or business
//                cost edit alone leaves the row an estimate.
//
// Either way the row's source becomes `user_confirmed` (shown as "Yours"),
// every estimate marker the trust rule reads is cleared, and the values it had
// are kept in `estimate_approval`. The trust rule itself is unchanged, so every
// producer and the Stage 5 certifier agree on the approved row without code of
// their own.
// ============================================================================

const ESTIMATE_PROVENANCE = 'ai review';

function lowerText(value) {
    return String(value ?? '').trim().toLowerCase();
}

/** True when the row carries any provenance marker isPendingReviewPricingItem treats as an estimate. */
export function hasEstimateProvenance(item) {
    if (!item) return false;
    return lowerText(item.source) === ESTIMATE_PROVENANCE
        || lowerText(item.origin) === ESTIMATE_PROVENANCE
        || lowerText(item.source_rating) === 'estimate'
        || lowerText(item.sourceRating) === 'estimate'
        || lowerText(item.source_note) === ESTIMATE_PROVENANCE
        || lowerText(item.sourceNote) === ESTIMATE_PROVENANCE;
}

/** Visible on the Pricing page, but not allowed to price a quote line until the builder approves it. */
export function isEstimatePricingItem(item) {
    return isVisiblePricingItem(item) && !isTrustedPricingItem(item) && hasEstimateProvenance(item);
}

/**
 * Remove every estimate marker, in every spelling the trust rule reads. Returns
 * a new row. Used by the approval below and by the rate-card refresh, which must
 * not turn an approved row back into an estimate.
 */
export function withoutEstimateMarkers(item) {
    const next = { ...item };
    for (const key of ['source_rating', 'sourceRating']) {
        if (lowerText(next[key]) === 'estimate') delete next[key];
    }
    for (const key of ['source_note', 'sourceNote', 'origin']) {
        if (lowerText(next[key]) === ESTIMATE_PROVENANCE) delete next[key];
    }
    return next;
}

const finiteOrNull = value => (value === null || value === undefined || value === '' || !Number.isFinite(Number(value))
    ? null
    : Number(value));

/**
 * The builder's approval of an estimate row. Returns a new row; when `before`
 * has no estimate provenance the row comes back as an unchanged copy.
 *
 * @param {object} item  the row to approve (an edit passes the edited row)
 * @param {object} meta
 * @param {'approve'|'rate_edit'} meta.action
 * @param {string} meta.approvedBy
 * @param {string} meta.approvedAt  ISO timestamp
 * @param {number|null} meta.rate   the estimate's rate before the builder acted
 * @param {object} meta.before      the row as stored before the builder acted (defaults to `item`)
 */
export function approveEstimatePricingItem(item, { action = 'approve', approvedBy = 'user', approvedAt, rate = null, before = item } = {}) {
    if (!hasEstimateProvenance(before)) return { ...item };
    const at = approvedAt || new Date().toISOString();
    const recorded = (snake, camel) => (before[snake] ?? before[camel] ?? null);
    return {
        ...withoutEstimateMarkers(item),
        source: 'user_confirmed',
        review_status: 'approved',
        reviewed_at: at,
        reviewed_by: approvedBy,
        estimate_approval: {
            action,
            approved_at: at,
            approved_by: approvedBy,
            source: before.source ?? null,
            origin: before.origin ?? null,
            source_rating: recorded('source_rating', 'sourceRating'),
            source_note: recorded('source_note', 'sourceNote'),
            rate: finiteOrNull(rate),
            unit: before.unit ?? null,
        },
    };
}
